import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { FadeUp, FadeIn, StaggerContainer, StaggerItem, AnimatedLine } from "../components/Animations";
import { ArrowRight, ChevronDown } from "lucide-react";

const HomePage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const portfolioSectors = [
    { title: "Investment Management", region: "UAE" },
    { title: "Film & Media Production", region: "UK" },
    { title: "Automotive Services", region: "UK" },
    { title: "Hospitality & Dining", region: "UK" },
    { title: "Poultry Farming", region: "Pakistan" },
    { title: "Agricultural Development", region: "Pakistan" },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative h-screen flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background Image with Parallax */}
        <motion.div
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0 z-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1525623434043-13387b9c56e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNvbmNyZXRlJTIwYXJjaGl0ZWN0dXJlJTIwbGlnaHQlMjBzaGFkb3d8ZW58MHx8fHwxNzcwNzY2OTYwfDA&ixlib=rb-4.1.0&q=85')`,
            }}
          />
          <div className="absolute inset-0 bg-champagne/70" />
        </motion.div>

        {/* Hero Content */}
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 container-wide text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <h1 className="text-display text-charcoal mb-8">
              Capital, structured with intent.
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-subhead max-w-2xl mx-auto"
          >
            A diversified holding group operating across media production, automotive services, 
            hospitality, agriculture, and investment management.
          </motion.p>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-mono text-stone/60">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="w-5 h-5 text-stone/60" />
          </motion.div>
        </motion.div>
      </section>

      {/* Identity Block */}
      <section className="section-spacing bg-white grain" data-testid="identity-section">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <FadeUp>
              <span className="text-caption text-bronze mb-6 block">Who We Are</span>
              <h2 className="text-headline text-charcoal mb-8">
                2 ON Capital — FZCO
              </h2>
              <p className="text-body text-lg mb-6">
                A knowledge-based investment and management firm headquartered in Dubai, 
                providing strategic advisory and operational support to small and medium-sized 
                businesses across multiple sectors and geographies.
              </p>
              <p className="text-body">
                Our group delivers tailored investment insights, project evaluation, capital advisory, 
                and commercial management services — supported by sector expertise from approved 
                professional partners.
              </p>
              <div className="mt-8">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
                >
                  <span className="text-sm font-sans font-medium uppercase tracking-wider">Learn More About Us</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </FadeUp>

            <FadeUp delay={0.2}>
              <div className="aspect-[4/3] relative overflow-hidden border-glass">
                <img
                  src="https://images.unsplash.com/photo-1770546597346-adb6f350812c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxzYW5kJTIwZHVuZXMlMjBtaW5pbWFsJTIwYmVpZ2V8ZW58MHx8fHwxNzcwNzY2OTY4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Abstract desert landscape"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-bronze/10" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="section-spacing" data-testid="global-presence-section">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Global Presence</span>
            <h2 className="text-headline text-charcoal mb-16">
              Operating across three strategic regions.
            </h2>
          </FadeUp>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8" staggerDelay={0.15}>
            {[
              {
                region: "United Arab Emirates",
                focus: "Investment & Management",
                path: "/portfolio/uae",
                desc: "Strategic advisory and commercial management"
              },
              {
                region: "United Kingdom",
                focus: "Media, Auto & Hospitality",
                path: "/portfolio/uk",
                desc: "Film production, automotive, and dining"
              },
              {
                region: "Pakistan",
                focus: "Agriculture & Poultry",
                path: "/portfolio/pakistan",
                desc: "Poultry farming and agricultural development"
              },
            ].map((item) => (
              <StaggerItem key={item.region}>
                <Link
                  to={item.path}
                  className="group block border-glass card-reflect p-8 md:p-12 h-full transition-all duration-500"
                  data-testid={`region-card-${item.region.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="text-mono text-stone/60 mb-4 block">Region</span>
                  <h3 className="text-2xl md:text-3xl font-serif text-charcoal mb-2 group-hover:text-bronze transition-colors duration-500">
                    {item.region}
                  </h3>
                  <p className="text-sm font-medium text-bronze mb-4">{item.focus}</p>
                  <p className="text-body text-sm mb-8">{item.desc}</p>
                  <div className="flex items-center gap-2 text-bronze">
                    <span className="text-sm font-sans font-medium uppercase tracking-wider">Explore</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-500" />
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Portfolio Snapshot */}
      <section className="section-spacing bg-charcoal" data-testid="portfolio-snapshot-section">
        <div className="container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Portfolio</span>
            <h2 className="text-headline text-champagne mb-6">
              8 companies. 6 sectors. 3 regions.
            </h2>
            <p className="text-lg font-sans font-light text-champagne/70 max-w-2xl mb-16">
              Each business operates independently within its specialist sector while 
              contributing to a diversified commercial portfolio.
            </p>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-champagne/10">
            {portfolioSectors.map((sector, index) => (
              <FadeUp key={sector.title} delay={index * 0.1}>
                <div className="bg-charcoal p-8 md:p-12 group hover:bg-charcoal/80 transition-colors duration-500">
                  <span className="text-mono text-champagne/40 mb-4 block">{sector.region}</span>
                  <h3 className="text-xl md:text-2xl font-serif text-champagne group-hover:text-bronze transition-colors duration-500">
                    {sector.title}
                  </h3>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.4} className="mt-12 text-center">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-3 text-champagne hover:text-bronze transition-colors duration-500"
              data-testid="view-portfolio-link"
            >
              <span className="text-sm font-sans font-medium uppercase tracking-wider">View Full Portfolio</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* Philosophy Teaser */}
      <section className="section-spacing" data-testid="philosophy-teaser-section">
        <div className="container-wide">
          <div className="max-w-4xl">
            <FadeUp>
              <span className="text-caption text-bronze mb-6 block">Philosophy</span>
              <h2 className="text-headline text-charcoal mb-8">
                Independent operation. Unified excellence.
              </h2>
            </FadeUp>

            <div className="space-y-8">
              <FadeUp delay={0.2}>
                <AnimatedLine className="mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <h4 className="font-serif text-xl text-charcoal">Sector Expertise</h4>
                  <p className="text-body md:col-span-2">
                    Each business operates independently within its specialist sector, 
                    maintaining focus and deep expertise in their respective industries.
                  </p>
                </div>
              </FadeUp>

              <FadeUp delay={0.3}>
                <AnimatedLine className="mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <h4 className="font-serif text-xl text-charcoal">Diversified Portfolio</h4>
                  <p className="text-body md:col-span-2">
                    Our diversified structure ensures resilience across market cycles 
                    while enabling strategic resource allocation and support.
                  </p>
                </div>
              </FadeUp>

              <FadeUp delay={0.4}>
                <AnimatedLine className="mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <h4 className="font-serif text-xl text-charcoal">Long-term Value</h4>
                  <p className="text-body md:col-span-2">
                    We build businesses with a multi-decade perspective, prioritising 
                    sustainable growth and enduring value creation over short-term gains.
                  </p>
                </div>
              </FadeUp>
            </div>

            <FadeUp delay={0.5} className="mt-12">
              <Link
                to="/philosophy"
                className="inline-flex items-center gap-3 text-charcoal hover:text-bronze transition-colors duration-500"
                data-testid="explore-philosophy-link"
              >
                <span className="text-sm font-sans font-medium uppercase tracking-wider">Explore Our Philosophy</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Legacy Section */}
      <section className="relative h-screen flex items-center" data-testid="legacy-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1682368724572-61e83de6c91f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxncmVlbiUyMGFncmljdWx0dXJlJTIwZmllbGQlMjBhZXJpYWx8ZW58MHx8fHwxNzcwNzY2OTgwfDA&ixlib=rb-4.1.0&q=85"
            alt="Aerial view of landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/60" />
        </div>

        <div className="relative z-10 container-wide">
          <FadeUp>
            <span className="text-caption text-bronze mb-6 block">Legacy</span>
            <h2 className="text-display text-champagne max-w-4xl">
              Building systems that outlast cycles.
            </h2>
          </FadeUp>

          <FadeUp delay={0.3} className="mt-12">
            <Link
              to="/legacy"
              className="inline-flex items-center gap-3 text-champagne hover:text-bronze transition-colors duration-500"
              data-testid="discover-legacy-link"
            >
              <span className="text-sm font-sans font-medium uppercase tracking-wider">Discover Our Vision</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
